import type { ReactNode } from "react";
import { AlertTriangle } from "./icons";
import { VerbatimErrorText } from "./verbatim-error-text";

/**
 * A refund the **provider answered and refused** (`009` R2).
 *
 * The error is retained, the order stays open, and nothing is retried automatically — retrying an
 * answer on a timer is how a gateway starts rate-limiting the portal. The Accounts user's next
 * action is to read the provider's reason, fix something, and decide.
 *
 * ## Why this is not `<RefundStatus status="failed" />`
 *
 * `RefundQueued` is its sibling, in its own file. Queued means the provider was never reached and
 * nothing is known; failed means it was reached and said no. They mean opposite things about what
 * the operator should do next, and a shared component with a status prop is how that gets flattened
 * into a colour and an interpolated word.
 *
 * ## The provider's text goes through `VerbatimErrorText`
 *
 * Escaped and unmodified (`010` R3). A friendlier paraphrase of "card scheme declined: issuer
 * unavailable" is a paraphrase of the only sentence that says what to do next.
 */
export function RefundFailed({
  amount,
  providerError,
  order,
  children,
}: {
  /** The refund amount, already formatted by the caller (§8.5, constitution I). */
  amount: ReactNode;
  /** The provider's retained text. Rendered verbatim; never summarised. */
  providerError: string;
  /**
   * A link to the still-open order. Required: the order is still open *because* this failed, and a
   * failure notice with no route to the thing it is about is a dead end.
   */
  order: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div
      role="alert"
      data-slot="refund-failed"
      className="border-red/30 bg-red/5 text-foreground flex gap-3.5 rounded-xl border p-4 text-sm"
    >
      <span className="bg-red/10 text-red-dark mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg">
        <AlertTriangle aria-hidden className="size-4" />
      </span>

      <div className="flex min-w-0 flex-col gap-2">
        <p className="font-semibold">Failed — the provider refused this refund.</p>
        <p className="text-muted-foreground">
          The provider answered. {amount} has not been returned to the customer, the order stays
          open, and nothing is retried automatically.
        </p>

        <VerbatimErrorText label="The provider's response">{providerError}</VerbatimErrorText>

        <div className="text-sm">{order}</div>
        {children ? <div>{children}</div> : null}
      </div>
    </div>
  );
}
